import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "react-query";
import axios from "axios";
import { Button } from "components/button";
import { useModalContext } from "components/modal";
import { useTranslation } from "react-i18next";
import ConfirmSendModal from "./ConfirmSendModal";
import { IPlan, ICustomerPlan, IProperty } from "../interface";
import { useSnackbarContext } from "components/snackbar";
import { FaRedo } from "react-icons/fa";
import { RiClipboardLine } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { format } from "date-fns";
import { FcDeleteDatabase } from "react-icons/fc";

interface IProps {
  customerId: string;
  customerEmail: string;
  plan: IPlan;
  customer: ICustomerPlan;
};

const PlanOptionModal = (props: IProps) => {
  const { t, i18n } = useTranslation();
  const tBase = "views.service-plans.components.PlanOptionModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  
  const queryClient = useQueryClient();
  const { setModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  

  const { customer, plan, customerId, customerEmail } = props;
  const { 
    customer_address: property,
    last_sent: lastSent,
    id: sptcId,
    archived,
    archived_by,
    archived_at, 
  } = customer;

  const [showButtons, setShowButtons] = useState({
    remove: false,
    archive: false
  }); // used when removing a plan
  const [archiveData, setArchiveData] = useState({
    archived: archived,
    archived_at: archived_at,
    archived_by: archived_by,
  });

  const { data: customerEmails } = useQuery(
    ["servicePlanEmails", plan.id],
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/emails`,
        { 
          params: {
            servicePlanId: plan.id,
            propertyId: property.id,
            customerId: customerId
          }
        }
      );
      return response.data;
    },
    {
      enabled: plan.is_contract, // Only fetch if the plan is a contract for the resend api post
    }
  );

  const removePlanFromCustomer = useMutation(
    async (info: {
      servicePlanId: string;
      customerId: string;
      propertyId: string;
    }) => {
      await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/delete-plan`, { 
          data: info 
        }).then((res) => res.data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        queryClient.invalidateQueries(["customerPlans", customerId, "plans"]);
        queryClient.invalidateQueries("plans");
        queryClient.invalidateQueries(["customer", customerId]);
        showSnackbar(tr("Plan removed from customer"));
      }
    }
  );

  const getArchiver = async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/get-archiver`, {
      params: { servicePlanId: sptcId },
    });
    if (res.status === 200) {
      setArchiveData(res.data);
    }
    return res.data;
  };

  const archiver = async () => {
    const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/archive`, {
      servicePlanId: sptcId,
      bool: !archiveData.archived,
    })
    if (res.status === 200) {
      await getArchiver();
      queryClient.invalidateQueries(["customerPlans", customerId, "plans"]);
      queryClient.invalidateQueries("plans");
      queryClient.invalidateQueries(["customer", customerId]);
      showSnackbar(archiveData.archived ? tr("Plan unarchived") : tr("Plan archived"));
    }
    return res.data;
  };

  useEffect(() => {
    if (archiveData.archived) {
      getArchiver(); 
    }
  }, []);

  const langHeight = i18n.language === "es" ? "h-[140px]" : "h-[100px]";

  return (
    <>
      <div className="flex justify-evenly gap-[5px] [&>*]:w-[200px] mb-10">
        {/* Resend Contract */}
        {plan.is_contract && (
          <button 
            className={`
              ${langHeight} p-3 border flex flex-col items-center justify-center 
              group hover:shadow-md select-none transition-shadow duration-200 ease-in-out relative
            `}
            onClick={() => {
              setModal({
                label: tr("Resend Contract"),
                component: (
                  <ConfirmSendModal 
                    servicePlanId={plan.id} 
                    propertyId={property.id} 
                    customerEmail={customerEmail} 
                    customerEmailsSaved={customerEmails.emails || []}
                    customerId={customerId} 
                    type="resend"
                  />
                ),
              });
            }}
          >
            <div className="relative">
              <RiClipboardLine size={65} color="#808080" />
              <FaRedo size={20} className="absolute top-[60%] right-1/2 translate-x-1/2 -translate-y-1/2 text-2xl text-blue-400 group-hover:scale-[1.15] transition-transform duration-200 ease-in-out" />
            </div>
            <div className="text-gray-600 group-hover:text-blue-400 transition-colors duration-200 ease-in-out">
              {tr("Resend contract email")}
            </div>
            {lastSent && (
              <div className="absolute -bottom-[20px] text-xs text-gray-500 ">
                {tr("Last sent")} {format(new Date(lastSent), 'MM/dd/yyyy')}
              </div>
            )}
          </button>
        )}
        {/* Archive/Unarchive Plan */}
        <button 
          className={`
            ${langHeight} p-3 border flex flex-col items-center justify-center relative
            group hover:shadow-md select-none transition-shadow duration-200 ease-in-out
            ${showButtons.archive ? "shadow-md" : "shadow-none"}
          `}
          onClick={() => setShowButtons({ remove: false, archive: true })}
        >
          <div className="relative">
            <RiClipboardLine size={65} color="#808080" />
            <div className={`
              absolute top-[60%] right-1/2 translate-x-1/2 -translate-y-1/2 text-2xl text-red-400 group-hover:scale-[1.15] transition-transform duration-200 ease-in-out
              ${showButtons.archive ? "scale-[1.15]" : "scale-100"}
              `} 
            >
                <FcDeleteDatabase size={28} className="mb-0.5" />
            </div>
          </div>
            <div className={`
              group-hover:text-red-400 transition-colors duration-200 ease-in-out
              ${showButtons.archive ? "text-red-400" : "text-gray-600"}
            `}>
              {archiveData.archived ? tr("Unarchive") : tr("Archive")}
              {archiveData.archived && (
                <div className="absolute -bottom-[36px] left-1/2 -translate-x-1/2 text-xs text-gray-500 w-full text-center ">
                  {tr("Archived by")} {archiveData.archived_by} 
                  {archiveData.archived_at && ` ${tr("on")} ${format(new Date(archiveData.archived_at), 'MM/dd/yyyy')}`}
                </div>
              )}
            </div>
        </button>
        {/* Remove Plan */}
        {archiveData.archived && (
          <button 
            className={`
              ${langHeight} p-3 border flex flex-col items-center justify-center 
              group hover:shadow-md select-none transition-shadow duration-200 ease-in-out
              ${showButtons.remove ? "shadow-md" : "shadow-none"}
            `}
            onClick={() => setShowButtons({ remove: true, archive: false })}
          >
            <div className="relative">
              <RiClipboardLine size={65} color="#808080" />
              <div className={`
                absolute top-[60%] right-1/2 translate-x-1/2 -translate-y-1/2 text-2xl text-red-400 group-hover:scale-[1.15] transition-transform duration-200 ease-in-out
                ${showButtons.remove ? "scale-[1.15]" : "scale-100"}
                `} 
              >
                <IoClose size={30} />
              </div>
            </div>
              <div className={`
                group-hover:text-red-400 transition-colors duration-200 ease-in-out
                ${showButtons.archive ? "text-red-400" : "text-gray-600"}
              `}>
                {tr("Remove Plan")}
              </div>
          </button>
        )}
      </div>
      {/* Buttons */}
      <div 
        className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${showButtons.archive || showButtons.remove ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}
        `}
      >
        <Button
          type="secondary"
          onClick={() => {
            setModal();
          }}
        >
          {tr("Cancel")}
        </Button>
        <Button
          style={{ float: "right" }}
          primary
          onClick={() => {
            if (showButtons.archive) {
              archiver();
            } else {
              removePlanFromCustomer.mutateAsync({
                servicePlanId: plan.id,
                customerId: customerId,
                propertyId: property.id,
              });
            }
            setModal();
          }}
        >
          {showButtons.archive 
            ? archiveData.archived ? tr("Unarchive") : tr("Archive") 
            : tr("Remove")
          }
        </Button>
      </div>
    </>
  );
};

export default PlanOptionModal;